import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock, ChevronRight, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * ZybekSplitEntry — sport-mode-aware split-distance picker for a SpeedIQ
 * session. Drives `POST /speediq/sessions/{id}/splits` and renders the
 * server-computed best/avg/rep rollup.
 */
const TIMING_SOURCES = [
  { v: "zybek_electronic_hand_entered", label: "Zybek electronic (hand entered)" },
  { v: "hand_timed_only",                label: "Hand-timed only" },
  { v: "mixed_split_source",             label: "Mixed sources" },
];

export default function ZybekSplitEntry({ sessionId, sportMode = "baseball", existingRollup = null, onSaved }) {
  const [splits, setSplits] = useState({});            // {distance_label: time_sec_string}
  const [repNo, setRepNo] = useState(1);
  const [timingSource, setTimingSource] = useState("hand_timed_only");
  const [legalSplits, setLegalSplits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/speediq/sport-modes");
        setLegalSplits(data.splits?.[sportMode] || []);
      } catch {/* fall back below */}
    })();
  }, [sportMode]);

  async function save() {
    const entries = Object.entries(splits)
      .filter(([, v]) => v && Number(v) > 0)
      .map(([label, v]) => {
        const meta = legalSplits.find(s => s.label === label) || { yd: parseFloat(label) || 10 };
        return {
          rep_no: repNo, distance_label: label,
          distance_yd_decimal: meta.yd, time_sec: Number(v),
          source: timingSource,
        };
      });
    if (!entries.length) {
      toast.error("Add at least one split time."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/speediq/sessions/${sessionId}/splits`, {
        rep_no: repNo, timing_source: timingSource, splits: entries,
      });
      setIssues(data.validation_issues || []);
      if ((data.validation_issues || []).length) {
        toast.warning(`Rep ${data.rep_no} saved · ${data.validation_issues.length} consistency note${data.validation_issues.length === 1 ? "" : "s"}.`);
      } else {
        toast.success(`Rep ${data.rep_no} saved.`);
      }
      onSaved?.(data.rollup);
      setRepNo(repNo + 1);
      setSplits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save splits.");
    } finally { setBusy(false); }
  }

  async function deleteRep(rn) {
    try {
      const { data } = await api.delete(`/speediq/sessions/${sessionId}/splits/${rn}`);
      toast.success(`Rep ${rn} deleted.`);
      onSaved?.(data.rollup);
    } catch {
      toast.error("Could not delete rep.");
    }
  }

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5 space-y-4" data-testid="zybek-split-entry">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Step 3 · Zybek splits</div>
          <div className="text-base font-display font-black text-white mt-0.5">
            Hand-enter timing-gate splits ({sportMode === "baseball" ? "10y · 30.6y Home-to-First · 60y" : "10y · 20y · 40y"})
          </div>
        </div>
        <Clock className="h-5 w-5 text-white/40" />
      </div>

      {/* Rep + source row */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-white/70">Rep#
          <Input type="number" min={1} max={20} value={repNo} onChange={(e) => setRepNo(Math.max(1, Number(e.target.value) || 1))}
                 className="ml-2 w-16 h-8 inline-block text-center font-mono-num" data-testid="zybek-rep-no" />
        </label>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-wider text-white/55 mb-1">Timing source</div>
          <div className="flex flex-wrap gap-1.5">
            {TIMING_SOURCES.map((s) => (
              <button key={s.v} onClick={() => setTimingSource(s.v)} data-testid={`zybek-src-${s.v}`}
                      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border
                                  ${timingSource === s.v
                                    ? "border-[var(--brand-secondary)]/70 bg-[var(--brand-secondary)]/15 text-white"
                                    : "border-white/15 bg-black/30 text-white/55 hover:text-white"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Split inputs */}
      <div className="grid grid-cols-3 gap-2">
        {legalSplits.map((s) => (
          <div key={s.label} className="rounded-lg p-3 bg-black/30 border border-white/10" data-testid={`zybek-split-${s.label}`}>
            <div className="text-[10px] uppercase tracking-wider text-white/55">{s.label} · {s.yd}yd</div>
            <Input type="number" step="0.01" min={0.5} max={30} placeholder="0.00s"
                   value={splits[s.label] || ""}
                   onChange={(e) => setSplits({ ...splits, [s.label]: e.target.value })}
                   className="mt-1 h-10 text-base font-mono-num text-center"
                   data-testid={`zybek-input-${s.label}`} />
            <div className="text-[10px] text-white/35 mt-1">seconds</div>
          </div>
        ))}
      </div>

      {issues.length > 0 && (
        <div className="rounded p-3 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-100" data-testid="zybek-issues">
          <b>Consistency notes ({issues.length}):</b>
          <ul className="mt-1 ml-5 list-disc">{issues.map((it, i) => <li key={i}>{it}</li>)}</ul>
        </div>
      )}

      <Button onClick={save} disabled={busy} className="w-full" style={{ backgroundColor: "var(--brand-secondary)" }}
              data-testid="zybek-save">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ChevronRight className="h-4 w-4 mr-2" />}
        Save rep {repNo}
      </Button>

      {/* Rollup */}
      {existingRollup && Object.keys(existingRollup.by_distance || {}).length > 0 && (
        <div className="rounded-lg p-4 border border-emerald-500/30 bg-emerald-500/5" data-testid="zybek-rollup">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-2">
            Session rollup · {existingRollup.rep_count} rep{existingRollup.rep_count === 1 ? "" : "s"}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {Object.entries(existingRollup.by_distance).map(([d, r]) => (
              <div key={d} className="rounded p-2 bg-black/30 border border-white/10" data-testid={`zybek-summary-${d}`}>
                <div className="text-[10px] uppercase tracking-wider text-white/55">{d}</div>
                <div className="font-display font-bold text-base mt-0.5 text-white">{r.best.toFixed(2)}<span className="text-[10px] text-white/40">s best</span></div>
                <div className="text-[11px] text-white/65">avg {r.avg.toFixed(2)}s · {r.count}×</div>
              </div>
            ))}
          </div>
          {existingRollup.by_rep?.length > 0 && (
            <div className="mt-3 space-y-1">
              {existingRollup.by_rep.map((r) => (
                <div key={r.rep_no} className="flex items-center justify-between text-[11px] text-white/70" data-testid={`zybek-rep-row-${r.rep_no}`}>
                  <span>Rep {r.rep_no} · total {r.total_time_sec.toFixed(2)}s · {r.split_count} splits</span>
                  <button onClick={() => deleteRep(r.rep_no)} className="text-white/40 hover:text-red-400" data-testid={`zybek-del-rep-${r.rep_no}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
